import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * <frequently-bought> — bundle widget.
 *
 * Recalculates the total as checkboxes/variants change (applying the bundle %
 * once 2+ items are selected), and on "Add to cart" posts the selected items
 * to /cart/add.js. When 2+ are selected each line gets a hidden `_bundle`
 * property so the Shopify Functions discount can take the % off.
 */
class FrequentlyBought extends HTMLElement {
  connectedCallback() {
    this.discount = parseFloat(this.dataset.discount || '0');
    this.minItems = parseInt(this.dataset.minItems || '2', 10);
    this.bundleId = 'frequently-bought';
    this.currency = this.dataset.currency || 'GBP';

    // The locked current-product line tracks the PDP variant selector. Its price
    // per variant comes from this map so we don't need to re-fetch on change.
    this.currentProductId = this.dataset.productId || null;
    try {
      this.currentPrices = JSON.parse(this.dataset.currentPrices || '{}');
    } catch {
      this.currentPrices = {};
    }

    this.items = [...this.querySelectorAll('[data-fbt-item]')];
    this.addButton = this.querySelector('[data-fbt-add]');
    this.totalEl = this.querySelector('[data-fbt-total]');
    this.wasEl = this.querySelector('[data-fbt-was]');

    this.money = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency });

    this.onChange = this.onChange.bind(this);
    this.onAdd = this.onAdd.bind(this);
    this.onVariantUpdate = this.onVariantUpdate.bind(this);

    this.addEventListener('change', this.onChange);
    this.addButton?.addEventListener('click', this.onAdd);
    // variant:update bubbles from the product form to the document.
    document.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);

    this.refreshRowPrices();
    this.recalculate();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.addButton?.removeEventListener('click', this.onAdd);
    document.removeEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate);
  }

  /** Keep the locked current-product line in sync with the PDP variant picker. */
  onVariantUpdate(event) {
    const detail = event.detail || {};
    if (!this.currentProductId || String(detail.data?.productId) !== String(this.currentProductId)) return;

    const variant = detail.resource;
    if (!variant?.id) return;

    const control = this.querySelector('[data-fbt-current] [data-fbt-variant]');
    if (!control) return;

    control.value = String(variant.id);
    const price = this.currentPrices[variant.id];
    if (price != null) control.dataset.price = String(price);

    this.refreshRowPrices();
    this.recalculate();
  }

  onChange(event) {
    // Keep each row's displayed price in sync with its selected variant.
    if (event.target.matches('[data-fbt-variant]')) this.refreshRowPrices();
    this.recalculate();
  }

  /** The variant control + current unit price (in minor units) for a row. */
  getRow(item) {
    const control = item.querySelector('[data-fbt-variant]');
    let variantId = null;
    let price = 0;
    if (control?.tagName === 'SELECT') {
      const option = control.options[control.selectedIndex];
      variantId = control.value;
      price = parseInt(option?.dataset.price || '0', 10);
    } else if (control) {
      variantId = control.value;
      price = parseInt(control.dataset.price || '0', 10);
    }
    // The current product is locked in — always counts as selected.
    const checked = item.hasAttribute('data-fbt-current')
      ? true
      : (item.querySelector('[data-fbt-check]')?.checked ?? false);
    return { variantId, price, checked };
  }

  refreshRowPrices() {
    for (const item of this.items) {
      const { price } = this.getRow(item);
      const priceEl = item.querySelector('[data-fbt-price]');
      if (priceEl) priceEl.textContent = this.money.format(price / 100);
    }
  }

  recalculate() {
    const selected = this.items.map((item) => this.getRow(item)).filter((row) => row.checked && row.variantId);
    const subtotal = selected.reduce((sum, row) => sum + row.price, 0);
    const applyDiscount = selected.length >= this.minItems && this.discount > 0;
    const total = applyDiscount ? Math.round(subtotal * (1 - this.discount / 100)) : subtotal;

    if (this.totalEl) this.totalEl.textContent = selected.length ? this.money.format(total / 100) : '';
    if (this.wasEl) {
      if (applyDiscount) {
        this.wasEl.textContent = this.money.format(subtotal / 100);
        this.wasEl.hidden = false;
      } else {
        this.wasEl.hidden = true;
      }
    }
    if (this.addButton) this.addButton.disabled = selected.length === 0;

    this.applyDiscount = applyDiscount;
    this.selected = selected;
  }

  async onAdd() {
    if (!this.selected?.length) return;

    const items = this.selected.map((row) => {
      const line = { id: Number(row.variantId), quantity: 1 };
      if (this.applyDiscount) line.properties = { _bundle: this.bundleId };
      return line;
    });

    this.addButton.setAttribute('aria-busy', 'true');

    try {
      const addRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!addRes.ok) throw new Error(`cart/add failed: ${addRes.status}`);

      const cartRes = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const cart = await cartRes.json();

      this.#trackAddToCart(cart, items);

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'frequently-bought', {
          source: 'frequently-bought',
          itemCount: cart.item_count,
        })
      );
    } catch (error) {
      console.error('[frequently-bought]', error);
    } finally {
      this.addButton.removeAttribute('aria-busy');
    }
  }

  /**
   * GA4 `add_to_cart` for the whole bundle. Titles/prices come from the cart
   * response; value is the (discounted) bundle total the shopper saw.
   * @param {any} cart - The cart.js response.
   * @param {Array<{id: number, quantity: number}>} lines - The lines we just posted.
   */
  #trackAddToCart(cart, lines) {
    const track = /** @type {any} */ (window).track;
    if (typeof track !== 'function' || !Array.isArray(cart?.items)) return;

    const addedIds = new Set(lines.map((line) => String(line.id)));
    const subtotal = (this.selected || []).reduce((sum, row) => sum + row.price, 0);
    const total = this.applyDiscount ? Math.round(subtotal * (1 - this.discount / 100)) : subtotal;

    const items = /** @type {any[]} */ (cart.items)
      .filter((line) => addedIds.has(String(line.variant_id)))
      .map((line, index) => ({
        item_id: String(line.product_id),
        item_name: line.product_title || line.title,
        item_variant: line.variant_title || undefined,
        price: line.price / 100,
        quantity: 1,
        index,
        item_list_name: 'frequently_bought',
      }));

    track('add_to_cart', {
      currency: this.currency,
      value: total / 100,
      item_list_name: 'frequently_bought',
      parent_product_id: this.currentProductId || undefined,
      parent_product_name: this.dataset.productTitle || undefined,
      items,
    });
  }
}

if (!customElements.get('frequently-bought')) {
  customElements.define('frequently-bought', FrequentlyBought);
}
